export type LocalizedMap = Record<string, string>;

function isLocalizedMap(value: unknown): value is LocalizedMap {
	if (!value || typeof value !== 'object') {
		return false;
	}
	const map = value as Record<string, unknown>;
	return Object.keys(map).some((k) => typeof k === 'string' && typeof map[k] === 'string');
}

function tryParseJson(text: string): unknown | null {
	try {
		return JSON.parse(text);
	}
	catch {
		return null;
	}
}

function extractJsonFromFencedBlock(input: string): string | null {
	const trimmed = input.trim();
	if (trimmed.startsWith('```')) {
		// ```i18n\n{ ... }\n```
		const fenceStart = trimmed.indexOf('{');
		const fenceEnd = trimmed.lastIndexOf('}');
		if (fenceStart !== -1 && fenceEnd !== -1 && fenceEnd > fenceStart) {
			return trimmed.substring(fenceStart, fenceEnd + 1);
		}
	}
	return null;
}

function extractInlineJson(input: string): string | null {
	const start = input.indexOf('{');
	const end = input.lastIndexOf('}');
	if (start !== -1 && end !== -1 && end > start) {
		return input.substring(start, end + 1);
	}
	return null;
}

export function resolveLocalizedText(input: unknown, currentLanguage?: string, fallbackLanguage = 'en'): string {
	if (input === null) {
		return '';
	}

	// Already a localized map from backend (e.g., Firestore map)
	if (isLocalizedMap(input)) {
		const map = input as LocalizedMap;
		const lang = currentLanguage || fallbackLanguage;
		return map[lang] || map[fallbackLanguage] || Object.values(map)[0] || '';
	}

	// Plain string or string-encoded JSON
	if (typeof input === 'string') {
		const trimmed = input.trim();

		// Try fenced block first
		const fencedJson = extractJsonFromFencedBlock(trimmed);
		if (fencedJson) {
			const parsed = tryParseJson(fencedJson);
			if (isLocalizedMap(parsed)) {
				const map = parsed as LocalizedMap;
				const lang = currentLanguage || fallbackLanguage;
				return map[lang] || map[fallbackLanguage] || Object.values(map)[0] || '';
			}
		}

		// Try to parse entire string as JSON
		const parsedWhole = tryParseJson(trimmed);
		if (isLocalizedMap(parsedWhole)) {
			const map = parsedWhole as LocalizedMap;
			const lang = currentLanguage || fallbackLanguage;
			return map[lang] || map[fallbackLanguage] || Object.values(map)[0] || '';
		}

		// Try inline JSON inside the string
		const inline = extractInlineJson(trimmed);
		if (inline) {
			const parsedInline = tryParseJson(inline);
			if (isLocalizedMap(parsedInline)) {
				const map = parsedInline as LocalizedMap;
				const lang = currentLanguage || fallbackLanguage;
				return map[lang] || map[fallbackLanguage] || Object.values(map)[0] || '';
			}
		}

		// Fallback: raw string
		return input;
	}

	return '';
}

